import { Link } from "react-router-dom";
import type { NavItem } from "@/lib/navDefaults";

interface NavMenuLinkProps {
  item: NavItem;
  className?: string;
  onAction?: (actionKey: string) => void;
  onNavigate?: () => void;
  /**
   * When provided, internal route/hash links call this instead of navigating
   * directly — lets the header keep its smooth-scroll behaviour.
   */
  onInternalNavigate?: (href: string) => void;
  children?: React.ReactNode;
}

/** Modifier/middle clicks must keep the browser's native open-in-new-tab. */
function isPlainLeftClick(e: React.MouseEvent) {
  return !e.defaultPrevented && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

/**
 * Renders a single navigation item across all link types so the header and
 * footer never duplicate link-resolution logic. Internal items always render a
 * real anchor (crawlable, copyable, openable in a new tab); the custom
 * scroll behaviour is layered on top of the href, never replacing it.
 */
export function NavMenuLink({
  item, className, onAction, onNavigate, onInternalNavigate, children,
}: NavMenuLinkProps) {
  const label = children ?? item.label;
  const href = item.href ?? "";

  if (item.link_type === "action") {
    return (
      <button
        type="button"
        className={className}
        onClick={() => {
          if (item.action_key) onAction?.(item.action_key);
          onNavigate?.();
        }}
      >
        {label}
      </button>
    );
  }

  if (item.link_type === "none") {
    return <span className={className}>{label}</span>;
  }


  const isExternal = item.link_type === "external" || /^https?:\/\//.test(href);
  const opensNewTab = item.open_in_new_tab || (isExternal && !href.startsWith("/"));

  if (isExternal || opensNewTab) {
    return (
      <a
        href={href}
        className={className}
        onClick={onNavigate}
        {...(opensNewTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {label}
      </a>
    );
  }

  // Fragment links resolve against the homepage so they work from any route.
  const isHash = href.startsWith("#") || href.startsWith("/#");
  const target = isHash ? (href.startsWith("/") ? href : `/${href}`) : href || "/";

  const handleClick = (e: React.MouseEvent) => {
    if (!isPlainLeftClick(e)) return;
    if (onInternalNavigate) {
      e.preventDefault();
      onInternalNavigate(target);
    }
    onNavigate?.();
  };

  if (isHash) {
    return (
      <a href={target} className={className} onClick={handleClick}>
        {label}
      </a>
    );
  }

  return (
    <Link to={target} className={className} onClick={handleClick}>
      {label}
    </Link>
  );
}
